import { createSlice } from "@reduxjs/toolkit";
import {
  FrameType0,
  FrameType1,
  SensorMap,
  SplitFrame,
  TyreConfig,
  TPMSState,
} from "../types";

import {} from "../types";
const selectedCarSave = localStorage.getItem("selectedCar");

const showPlotsSave = localStorage.getItem("showPlots");
const showPlots = showPlotsSave ? showPlotsSave === "true" : true;
if (!showPlotsSave) {
  localStorage.setItem("showPlots", showPlots ? "true" : "false");
}

const selectedPlotSave = localStorage.getItem("selectedPlot");
const selectedPlot = selectedPlotSave ? selectedPlotSave : "air";
if (!selectedPlotSave) {
  localStorage.setItem("selectedPlot", selectedPlot);
}

const showPredictionSave = localStorage.getItem("showPrediction");
const showPrediction = showPredictionSave
  ? showPredictionSave === "true"
  : false;
if (!showPredictionSave) {
  localStorage.setItem("showPrediction", showPrediction ? "true" : "false");
}

const pressureYMinSave = localStorage.getItem("pressureYMin");
const pressureYMaxSave = localStorage.getItem("pressureYMax");
const tempYMinSave = localStorage.getItem("tempYMin");
const tempYMaxSave = localStorage.getItem("tempYMax");
const xAxisMinSave = localStorage.getItem("xAxisMin");
const xAxisMaxSave = localStorage.getItem("xAxisMax");

const pressureYMin = pressureYMinSave ? Number(pressureYMinSave) : undefined;
const pressureYMax = pressureYMaxSave ? Number(pressureYMaxSave) : undefined;
const tempYMin = tempYMinSave ? Number(tempYMinSave) : undefined;
const tempYMax = tempYMaxSave ? Number(tempYMaxSave) : undefined;
const xAxisMin = xAxisMinSave ? Number(xAxisMinSave) : undefined;
const xAxisMax = xAxisMaxSave ? Number(xAxisMaxSave) : undefined;

const initialState: TPMSState = {
  usingAKSWeather: true,
  activeTyreData: { front: [], rear: [] },
  allTyreData: { front: [], rear: [] },
  loadingState: true,
  historicalDataLoaded: false,
  editModeState: false,
  selectedCar: selectedCarSave,
  selectedPlot: selectedPlot,
  tpmsData: { frameType0: [], frameType1: [] },
  tpmsConfig: null,
  // wsConnected: false,
  tpmsReceiverConnected: false,
  errorMessage: null,
  showPrediction: showPrediction,
  showPlots: showPlots,
  pressureYMin: pressureYMin,
  pressureYMax: pressureYMax,
  tempYMin: tempYMin,
  tempYMax: tempYMax,
  xAxisMin: xAxisMin,
  xAxisMax: xAxisMax,
};

function mergeObjects(a: any, b: any) {
  if (b === null) {
    return null;
  }

  if (a === null) {
    a = {};
  }

  if (Array.isArray(b)) {
    return b;
  }

  for (const key in b) {
    if (b[key] === null) {
      a[key] = null;
    } else if (typeof a[key] === "object" && typeof b[key] === "object") {
      a[key] = mergeObjects(a[key], b[key]);
    } else if (a[key] !== b[key]) {
      a[key] = b[key];
    }
  }

  return a;
}

function getEmptySensor(dt: number | null, SN: string) {
  return {
    dt: dt,
    SN: SN,
    RSSI: null,
    NTX: null,
    srType: null,
    srPos: null,
    tyreType: null,
    teamId: null,
    carId: null,
    pwrMode: null,
    dxs: null,
    TBoard: null,
    pAbs: null,
    VBat: null,
    pctBat: null,
    TAir: null,
    RH: null,
    TRim: null,
    TIR1: null,
    TIR2: null,
    TIR3: null,
    TIR4: null,
    TIR5: null,
  };
}

function mergeSensors(
  SN: string,
  frameType0: FrameType0 | null,
  frameType1: FrameType1 | null,
) {
  const data = getEmptySensor(null, SN);
  let latestTime = null;
  if (frameType0) {
    mergeObjects(data, frameType0);
    if (frameType0.dt) latestTime = frameType0.dt;
  }
  if (frameType1) {
    mergeObjects(data, frameType1);
    if (!latestTime || frameType1.dt > latestTime) {
      latestTime = frameType1.dt;
    }
  }
  data.dt = latestTime;
  return data;
}

function filterActiveData(state: TPMSState, isFront: boolean) {
  const tpmsConfig = state.tpmsConfig;
  const selectedCar = state.selectedCar;
  const tpmsData = state.tpmsData;
  if (tpmsConfig) {
    const tyreConfig = tpmsConfig["tyres"];
    return tyreConfig.reduce(function (
      filtered: SensorMap[],
      tyre: TyreConfig,
    ) {
      if (
        tpmsConfig &&
        tyre.carId === selectedCar &&
        tyre.isFront === isFront
      ) {
        const latestFrameType0 =
          tpmsData["frameType0"]
            .slice()
            .reverse()
            .find((x: FrameType0) => x.SN === tyre.sensorId) || null;
        const latestFrameType1 =
          tpmsData["frameType1"]
            .slice()
            .reverse()
            .find((x: FrameType1) => x.SN === tyre.sensorId) || null;

        const data: SensorMap = {
          tyreId: tyre.tyreId,
          sensorData: mergeSensors(
            tyre.sensorId,
            latestFrameType0,
            latestFrameType1,
          ),
        };

        filtered.push(data);
      }
      return filtered;
    },
    []);
  }
  return [];
}

function filterAllData(state: TPMSState, isFront: boolean) {
  const tpmsConfig = state.tpmsConfig;
  const selectedCar = state.selectedCar;
  const tpmsData = state.tpmsData;
  const data = [] as SplitFrame[];
  if (tpmsConfig) {
    const tyreConfig = tpmsConfig["tyres"];
    const thisGroupTyres = tyreConfig
      .filter(
        (x: TyreConfig) => x.carId === selectedCar && x.isFront === isFront,
      )
      .map((x: TyreConfig) => {
        return { sn: x.sensorId, id: x.tyreId };
      });
    for (const tyre of thisGroupTyres) {
      data.push({
        tyreId: tyre.id,
        frameType0: tpmsData["frameType0"].filter((x: FrameType0) => {
          return x.SN === tyre.sn;
        }),
        frameType1: tpmsData["frameType1"].filter((x: FrameType1) => {
          return x.SN === tyre.sn;
        }),
      });
    }
  }
  return data;
}

function initialiseTyreData(state: TPMSState) {
  state.activeTyreData.front = filterActiveData(state, true);
  state.activeTyreData.rear = filterActiveData(state, false);
  state.allTyreData.front = filterAllData(state, true);
  state.allTyreData.rear = filterAllData(state, false);
}

function updateActiveData(state: TPMSState, frame: FrameType0 | FrameType1) {
  const front = state.activeTyreData.front.find(
    (x: SensorMap) => x.sensorData.SN === frame.SN,
  );
  if (front) {
    mergeObjects(front.sensorData, frame);
  } else {
    const rear = state.activeTyreData.rear.find(
      (x: SensorMap) => x.sensorData.SN === frame.SN,
    );
    if (rear) {
      mergeObjects(rear.sensorData, frame);
    }
  }
}

function updateAllData(
  state: TPMSState,
  frameType: 0 | 1,
  frame: FrameType0 | FrameType1,
) {
  const tpmsConfig = state.tpmsConfig;
  if (tpmsConfig) {
    const tyreConfig = tpmsConfig["tyres"];
    const SN = frame.SN;
    const tyre = tyreConfig.find((x: TyreConfig) => x.sensorId === SN);
    if (tyre) {
      const car = tyre.carId;
      if (state.selectedCar === car) {
        const isFront = tyre.isFront;
        const idx = tyreConfig
          .filter(
            (x: TyreConfig) =>
              x.carId === state.selectedCar && x.isFront === isFront,
          )
          .findIndex((x: TyreConfig) => x.sensorId === SN);
        state.allTyreData[isFront ? "front" : "rear"][idx][
          frameType === 0 ? "frameType0" : "frameType1"
        ].push(frame as FrameType0 & FrameType1);
      }
    }
  }
}

function updateTyreData(
  state: TPMSState,
  frameType: 0 | 1,
  frame: FrameType0 | FrameType1,
) {
  updateActiveData(state, frame);
  updateAllData(state, frameType, frame);
}

export const tpmsSlice = createSlice({
  name: "tpms",
  initialState,
  reducers: {
    setUsingAKSWeather: (state, action) => {
      state.usingAKSWeather = action.payload;
    },
    setLoadingState: (state, action) => {
      state.loadingState = action.payload;
    },
    setHistoricalDataLoaded: (state, action) => {
      state.historicalDataLoaded = action.payload;
    },
    setEditModeState: (state, action) => {
      state.editModeState = action.payload;
    },
    setSelectedCar: (state, action) => {
      localStorage.setItem("selectedCar", action.payload);
      state.selectedCar = action.payload;
      initialiseTyreData(state);
    },
    setSelectedPlot: (state, action) => {
      localStorage.setItem("selectedPlot", action.payload);
      state.selectedPlot = action.payload;
      initialiseTyreData(state);
    },
    setTPMSData: (state, action) => {
      state.tpmsData = action.payload;
      initialiseTyreData(state);
    },
    updateTPMSData: (state, action) => {
      const frameType = action.payload.frameType;
      const frame = action.payload.frame;
      const frameArray =
        state.tpmsData[frameType === 0 ? "frameType0" : "frameType1"];

      // Find existing frame with same SN and dt
      const existingIdx = frameArray.findIndex(
        (f) => f.SN === frame.SN && f.dt === frame.dt,
      );

      if (existingIdx >= 0) {
        // Overwrite existing entry with new frame
        frameArray[existingIdx] = frame;
      } else {
        // No existing frame, push as new
        frameArray.push(frame);
      }

      updateTyreData(state, frameType, frame);
    },
    setTPMSConfig: (state, action) => {
      state.tpmsConfig = mergeObjects(state.tpmsConfig, action.payload);
      if (Object.prototype.hasOwnProperty.call(action.payload, "tyres")) {
        initialiseTyreData(state);
      }
    },
    setReceiverState: (state, action) => {
      state.tpmsReceiverConnected = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
    setShowPrediction: (state, action) => {
      localStorage.setItem("showPrediction", action.payload);
      state.showPrediction = action.payload;
    },
    setShowPlots: (state, action) => {
      localStorage.setItem("showPlots", action.payload ? "true" : "false");
      state.showPlots = action.payload;
    },
    setPressureYMin: (state, action) => {
      localStorage.setItem(
        "pressureYMin",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.pressureYMin = action.payload;
    },
    setPressureYMax: (state, action) => {
      localStorage.setItem(
        "pressureYMax",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.pressureYMax = action.payload;
    },
    setTempYMin: (state, action) => {
      localStorage.setItem(
        "tempYMin",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.tempYMin = action.payload;
    },
    setTempYMax: (state, action) => {
      localStorage.setItem(
        "tempYMax",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.tempYMax = action.payload;
    },
    setXAxisMin: (state, action) => {
      localStorage.setItem(
        "xAxisMin",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.xAxisMin = action.payload;
    },
    setXAxisMax: (state, action) => {
      localStorage.setItem(
        "xAxisMax",
        action.payload !== undefined ? action.payload.toString() : "",
      );
      state.xAxisMax = action.payload;
    },
  },
});

export const {
  setUsingAKSWeather,
  setLoadingState,
  setHistoricalDataLoaded,
  setEditModeState,
  setTPMSConfig,
  setTPMSData,
  updateTPMSData,
  setSelectedCar,
  setSelectedPlot,
  // setWsConnectedState,
  setReceiverState,
  setErrorMessage,
  setShowPrediction,
  setShowPlots,
  setPressureYMin,
  setPressureYMax,
  setTempYMin,
  setTempYMax,
  setXAxisMin,
  setXAxisMax,
} = tpmsSlice.actions;
export default tpmsSlice.reducer;
